/**
 * Brief builder — Step 1. Collects the wizard selections into ONE structured
 * object with resolved, human-readable values. This is exactly what gets
 * sent to OpenAI (via the prompt) — the model's entire understanding of what
 * to design. No deterministic design decisions are made here.
 */

import { taxonomyEngine } from '../taxonomy';
import { getDocumentKnowledgeByName } from '../documentKnowledge';

const nameOf = (fieldId, selections) =>
  taxonomyEngine.getItem(fieldId, selections[fieldId])?.name || null;

/**
 * Build the generation brief from wizard selections.
 * Every value is a display string (or null). `canvas` and `sections` come
 * from the document knowledge so the model designs to the right size and
 * knows the expected content blocks — but it is free to compose them.
 */
/** Drop empty strings/objects so the prompt never renders "Brand name: ". */
const present = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (typeof value === 'object') return Object.values(value).some(present);
  return true;
};

const compact = (obj) => Object.fromEntries(
  Object.entries(obj || {}).filter(([, v]) => present(v)),
);

/**
 * Build the generation brief from wizard selections.
 *
 * @param {Object} selections taxonomy selections
 * @param {Object} [extra]    { brand, settings } — the wizard's non-taxonomy
 *   fields. Optional so every existing caller keeps working unchanged.
 */
export const buildGenerationBrief = (selections, extra = {}) => {
  const knowledge = selections.documentType
    ? getDocumentKnowledgeByName(selections.documentType)
    : null;
  const size = knowledge?.recommendedSizes?.[0] || { width: 1080, height: 1080 };

  return {
    documentType: selections.documentType || null,
    industry: nameOf('industry', selections),
    category: nameOf('category', selections),
    purpose: nameOf('purpose', selections),
    audience: nameOf('audience', selections),
    brandPersonality: nameOf('brandPersonality', selections),
    visualTheme: nameOf('visualTheme', selections),
    layoutPreference: nameOf('layoutStyle', selections),
    imageStyle: nameOf('imageStyle', selections),
    colorMood: nameOf('colorMood', selections),
    typographyStyle: nameOf('typographyStyle', selections),
    canvas: { width: size.width, height: size.height },
    expectedSections: knowledge ? [...knowledge.typicalSections] : [],
    orientation: knowledge?.supportedOrientations?.[0] || 'portrait',

    // Brand identity and generation preferences. Empty values are dropped so
    // an untouched field never reaches the model as a blank instruction.
    brand: compact({
      region: extra.brand?.region,
      language: extra.brand?.language,
      campaignTitle: extra.brand?.campaignTitle,
      brandName: extra.brand?.brandName,
      logo: extra.brand?.logo,
      colors: compact(extra.brand?.brandColors),
      contact: compact(extra.brand?.contactDetails),
    }),
    settings: compact({
      quality: extra.settings?.quality,
      variation: extra.settings?.variation,
      images: extra.settings?.images,
    }),
  };
};
